import { useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { mdiPencilOutline } from '@mdi/js'
import { Formik, FormikHelpers } from 'formik'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { JobPageContext } from 'features/jobs/JobPageContext'
import { jobInfoLocationSelector } from 'features/jobs/selectors'
import { LocalizationContext } from 'components/i18n/LocalizationWrapper/LocalizationWrapper'
import {
  getGoogleMapLink,
  getJobLocation,
  GoogleMapsDataFromAddress,
  GoogleMapsDataFromLatLng,
} from 'components/GoogleMaps/utils'
import AsyncDropdownField from 'components/Fields/AsyncDropdownField/AsyncDropdownField'
import { fetchJobInfoThunk, updateJobThunk } from 'features/jobs/slice'
import { commonMessages } from 'app/i18n/messages'
import { SpinnerCentered } from 'components/Loader'
import { GoogleMapLocation } from 'components/GoogleMaps/types'
import GoogleMap from 'components/GoogleMaps/GoogleMap'
import { CommonLink } from 'components/Button'
import { JobDetailsLocationMessages } from './messages'
import {
  JobDetailsGoogleMapContainer,
  JobDetailsLocationButtonsWrapper,
  JobDetailsLocationCancelButton,
  JobDetailsLocationContainer,
  JobDetailsLocationEditButton,
  JobDetailsLocationForm,
  JobDetailsLocationFormContent,
  JobDetailsLocationFormControl,
  JobDetailsLocationHeader,
  JobDetailsLocationStyledIcon,
  JobDetailsLocationSubmitButton,
  JobDetailsLocationTitle,
} from './styles'

interface JobLocationValues {
  location: GoogleMapLocation | undefined
}

function JobDetailsLocation() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const { jobId } = useContext(JobPageContext)
  const { locale } = useContext(LocalizationContext)
  const currentLocation = useAppSelector(jobInfoLocationSelector)

  const [initialGoogleMapLocation, setInitialGoogleMapLocation] =
    useState<GoogleMapLocation>()
  const [currentLatitude, setCurrentLatitude] = useState<number>(
    currentLocation?.latitude
  )
  const [currentLongitude, setCurrentLongitude] = useState<number>(
    currentLocation?.longitude
  )
  const [editMode, setEditMode] = useState<boolean>(false)
  const [error, setError] = useState()

  const initialDropdownValue = useMemo(
    () => ({
      label: initialGoogleMapLocation?.formatted_address,
      value: initialGoogleMapLocation,
    }),
    [initialGoogleMapLocation]
  )

  useEffect(() => {
    const getInitialGoogleMapLocation = async () => {
      try {
        const googleMapsData = await GoogleMapsDataFromLatLng(
          currentLatitude,
          currentLongitude,
          locale
        )
        // Google can return several addresses for provided coordinates and we are taking the first one - results?.[0]
        setInitialGoogleMapLocation(googleMapsData?.results?.[0])
      } catch (err) {
        setError(err)
        console.log('Error getting initial google maps data', err)
      }
    }

    if (currentLatitude && currentLongitude) {
      getInitialGoogleMapLocation()
    }
  }, [locale, currentLatitude, currentLongitude])

  const handleSubmit = async (
    values: JobLocationValues,
    { setSubmitting }: FormikHelpers<JobLocationValues>
  ) => {
    const { location: newLocation } = values
    if (newLocation) {
      const jobLocation = getJobLocation(
        newLocation,
        currentLocation?.homeId,
        currentLocation?.ipAddress
      )
      try {
        setSubmitting(true)
        await dispatch(
          updateJobThunk({
            domainId: jobId,
            payload: { location: jobLocation },
          })
        )
        await dispatch(fetchJobInfoThunk(jobId))
      } catch (err) {
        setError(err)
        console.log('Error updating job', err)
      } finally {
        setSubmitting(false)
        setEditMode(false)
      }
    }
  }

  const loadOptions = async (inputValue: string) => {
    if (!inputValue) {
      return []
    }
    try {
      const googleMapsData = await GoogleMapsDataFromAddress(inputValue, locale)
      const addresses = googleMapsData.results || []

      return addresses.map((address: GoogleMapLocation) => ({
        value: address,
        label: address.formatted_address,
      }))
    } catch (err) {
      setError(err)
      console.log('Error uploading dropdown options from google maps', err)
    }
  }

  return (
    <JobDetailsLocationContainer>
      <Formik
        initialValues={{ location: initialGoogleMapLocation }}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => (
          <>
            <JobDetailsLocationHeader>
              <JobDetailsLocationTitle>
                {formatMessage(JobDetailsLocationMessages.title)}
              </JobDetailsLocationTitle>
              {!editMode && (
                <JobDetailsLocationEditButton
                  onClick={() => {
                    setError(undefined)
                    setEditMode(true)
                  }}
                >
                  <JobDetailsLocationStyledIcon path={mdiPencilOutline} />
                </JobDetailsLocationEditButton>
              )}
            </JobDetailsLocationHeader>
            <JobDetailsLocationForm>
              <JobDetailsLocationFormContent>
                <AsyncDropdownField
                  initialValue={initialDropdownValue}
                  name="location"
                  placeholder={formatMessage(
                    JobDetailsLocationMessages.addressPlaceholder
                  )}
                  loadOptions={loadOptions}
                  showLabel
                  disabled={!editMode}
                  handleChange={(location: any) => {
                    setCurrentLatitude(location?.value?.geometry?.location?.lat)
                    setCurrentLongitude(
                      location?.value?.geometry?.location?.lng
                    )
                  }}
                />
              </JobDetailsLocationFormContent>

              <JobDetailsGoogleMapContainer>
                {currentLongitude && currentLatitude && (
                  <CommonLink
                    as="a"
                    href={getGoogleMapLink(currentLatitude, currentLongitude)}
                    target="_blank"
                  >
                    <GoogleMap
                      latitude={currentLatitude}
                      longitude={currentLongitude}
                    />
                  </CommonLink>
                )}
              </JobDetailsGoogleMapContainer>

              {error && (
                <JobDetailsLocationTitle>
                  {formatMessage(commonMessages.commonError)}
                </JobDetailsLocationTitle>
              )}

              {editMode && (
                <JobDetailsLocationFormControl>
                  {formikProps.isSubmitting && (
                    <JobDetailsLocationButtonsWrapper>
                      <SpinnerCentered size="40" />
                    </JobDetailsLocationButtonsWrapper>
                  )}
                  {!formikProps.isSubmitting && (
                    <JobDetailsLocationButtonsWrapper>
                      <JobDetailsLocationCancelButton
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          formikProps.resetForm()
                          setEditMode(false)
                          setCurrentLatitude(currentLocation?.latitude)
                          setCurrentLongitude(currentLocation?.longitude)
                        }}
                      >
                        {formatMessage(commonMessages.cancel)}
                      </JobDetailsLocationCancelButton>
                      <JobDetailsLocationSubmitButton
                        variant="primary"
                        type="submit"
                        disabled={!formikProps?.values?.location}
                      >
                        {formatMessage(commonMessages.save)}
                      </JobDetailsLocationSubmitButton>
                    </JobDetailsLocationButtonsWrapper>
                  )}
                </JobDetailsLocationFormControl>
              )}
            </JobDetailsLocationForm>
          </>
        )}
      </Formik>
    </JobDetailsLocationContainer>
  )
}

export default JobDetailsLocation
